<template>
  <div class="params-settings">
    <!-- 顶部：能量链选择 + 命令记录 -->
    <div class="ps-toolbar">
      <div class="ps-toolbar__left">
        <span class="ps-toolbar__label">{{ $str.getKey('参数设置/能量链') }}</span>
        <v-select v-model="eLink" :items="eLinks" item-title="equipmentName" item-value="uuid" :disabled="isEdit"
          variant="outlined" density="compact" hide-details rounded="pill" class="ps-elink-select"
          :placeholder="$str.getKey('参数设置/能量链')" @update:model-value="() => updateData(false)" />
      </div>
      <v-btn variant="outlined" class="ps-btn-outline" @click="openRecord">
        <v-icon start size="16">mdi-history</v-icon>{{ $str.getKey('参数设置/命令记录') }}
      </v-btn>
    </div>

    <!-- 参数设置 -->
    <div class="ps-panel">
      <div class="ps-panel__title">{{ $str.getKey('参数设置/参数设置') }}</div>
      <div class="ps-panel__body ps-param-body">
        <!-- 左侧：基础参数 -->
        <div class="ps-param-list">
          <!-- 箱变容量配比 -->
          <div class="ps-param-item">
            <div class="ps-param-item__label">{{ $str.getKey('参数设置/箱变容量配比') }}</div>
            <div class="ps-param-item__value">
              <span>{{ numFormatter(formValues.transf_cap_ratio) }}</span>
              <v-menu v-model="visible.transf_cap_ratio" :close-on-content-click="false" location="bottom start"
                offset="6" :disabled="isEdit">
                <template #activator="{ props }">
                  <v-btn v-bind="props" variant="text" size="small" color="primary" class="ps-edit-btn"
                    @click="setValue('transf_cap_ratio')">
                    <v-icon start size="14">mdi-pencil-outline</v-icon>{{ $str.getKey('参数设置/修改') }}
                  </v-btn>
                </template>
                <v-card class="ps-edit-pop" elevation="0">
                  <div class="ps-edit-pop__title">{{ $str.getKey('参数设置/设置箱变容量配比') }}</div>
                  <v-text-field v-model.number="settingsValue.transf_cap_ratio" type="number" :min="0" :max="1"
                    :step="0.01" variant="outlined" density="compact" hide-details rounded="lg" />
                  <div class="ps-edit-pop__actions">
                    <v-btn variant="text" size="small" @click="cancelValue('transf_cap_ratio')">
                      {{ $str.getKey('参数设置/取消') }}
                    </v-btn>
                    <v-btn color="primary" size="small" @click="confirmValue('transf_cap_ratio')">
                      {{ $str.getKey('参数设置/确定') }}
                    </v-btn>
                  </div>
                </v-card>
              </v-menu>
            </div>
          </div>

          <!-- 最小需量 -->
          <div class="ps-param-item">
            <div class="ps-param-item__label">{{ $str.getKey('参数设置/最小需量') }}</div>
            <div class="ps-param-item__value">
              <span>{{ formValues.demand_min }} <em class="ps-unit">kW</em></span>
              <v-menu v-model="visible.demand_min" :close-on-content-click="false" location="bottom start" offset="6"
                :disabled="isEdit">
                <template #activator="{ props }">
                  <v-btn v-bind="props" variant="text" size="small" color="primary" class="ps-edit-btn"
                    @click="setValue('demand_min')">
                    <v-icon start size="14">mdi-pencil-outline</v-icon>{{ $str.getKey('参数设置/修改') }}
                  </v-btn>
                </template>
                <v-card class="ps-edit-pop" elevation="0">
                  <div class="ps-edit-pop__title">{{ $str.getKey('参数设置/最小需量') }}</div>
                  <v-text-field v-model.number="settingsValue.demand_min" type="number" :min="-3000" :max="3000"
                    :step="0.1" variant="outlined" density="compact" hide-details rounded="lg" suffix="kW" />
                  <div class="ps-edit-pop__actions">
                    <v-btn variant="text" size="small" @click="cancelValue('demand_min')">
                      {{ $str.getKey('参数设置/取消') }}
                    </v-btn>
                    <v-btn color="primary" size="small" @click="confirmValue('demand_min')">
                      {{ $str.getKey('参数设置/确定') }}
                    </v-btn>
                  </div>
                </v-card>
              </v-menu>
            </div>
          </div>

          <!-- 无功工作模式 -->
          <div class="ps-param-item">
            <div class="ps-param-item__label">{{ $str.getKey('参数设置/无功工作模式') }}</div>
            <div class="ps-param-item__value">
              <span>{{ reactiveModeDesc(formValues.reactive_mode) }}</span>
              <v-menu v-model="visible.reactive_mode" :close-on-content-click="false" location="bottom start" offset="6"
                :disabled="isEdit">
                <template #activator="{ props }">
                  <v-btn v-bind="props" variant="text" size="small" color="primary" class="ps-edit-btn"
                    @click="setReactiveValue()">
                    <v-icon start size="14">mdi-pencil-outline</v-icon>{{ $str.getKey('参数设置/修改') }}
                  </v-btn>
                </template>
                <v-card class="ps-edit-pop" elevation="0">
                  <div class="ps-edit-pop__title">{{ $str.getKey('参数设置/无功工作模式') }}</div>
                  <v-select v-model="settingsValue.reactive_mode" :items="reactiveModeOptions" item-title="label"
                    item-value="value" variant="outlined" density="compact" hide-details rounded="lg" />
                  <div class="ps-edit-pop__actions">
                    <v-btn variant="text" size="small" @click="cancelValue('reactive_mode')">
                      {{ $str.getKey('参数设置/取消') }}
                    </v-btn>
                    <v-btn color="primary" size="small" @click="confirmValue('reactive_mode')">
                      {{ $str.getKey('参数设置/确定') }}
                    </v-btn>
                  </div>
                </v-card>
              </v-menu>
            </div>
          </div>
        </div>

        <!-- 右侧：设置协议帧 -->
        <div class="ps-frame">
          <div class="ps-frame__label">{{ $str.getKey('参数设置/设置协议帧') }}</div>
          <v-textarea v-model="formValues.eblock_setting" :placeholder="$str.getKey('参数设置/请输入协议帧')" variant="outlined"
            rows="10" no-resize hide-details rounded="lg" :disabled="isEdit" class="ps-frame__input" style="font-family: 'HarmonyOS Sans SC', sans-serif;" />
          <div class="ps-frame__actions">
            <v-btn color="primary" class="ps-btn-primary" :disabled="formValues.eblock_setting.length == 0"
              @click="onSetting">
              {{ $str.getKey('参数设置/发送') }}
            </v-btn>
          </div>
        </div>
      </div>
    </div>

    <!-- eBlock 启停 -->
    <div class="ps-panel">
      <div class="ps-panel__title">{{ $str.getKey('参数设置/eBlock启停') }}</div>
      <div class="ps-panel__body">
        <div v-if="eBlockList.length > 0" class="ps-eblock-list">
          <div class="ps-eblock-card" v-for="eBlock in eBlockList" :key="eBlock.uuid">
            <div class="ps-eblock-card__head">
              <EBlockStatus :status="eBlockStatus[eBlock.uuid]"></EBlockStatus>
              <span class="ps-eblock-card__name">{{ eBlock.equipmentName }}</span>
            </div>
            <div class="ps-eblock-card__actions">
              <v-btn size="small" color="success" variant="tonal" class="ps-eblock-btn"
                @click="enableEBlock(eBlock, 1)">
                {{ $str.getKey('参数设置/启用') }}
              </v-btn>
              <v-btn size="small" color="error" variant="tonal" class="ps-eblock-btn" @click="enableEBlock(eBlock, 0)">
                {{ $str.getKey('参数设置/停用') }}
              </v-btn>
            </div>
          </div>
        </div>
        <div v-else class="ps-empty">
          <v-icon size="40" color="#c2cae2">mdi-inbox-outline</v-icon>
          <span>{{ $str.getKey('参数设置/暂无数据') }}</span>
        </div>
      </div>
    </div>

    <!-- 命令记录弹窗 -->
    <v-dialog v-model="isOpen" width="1000">
      <v-card class="ps-record-card" rounded="xl" :loading="loading">
        <div class="ps-record-card__header">
          <span class="ps-record-card__title">{{ $str.getKey('参数设置/命令记录') }}</span>
          <v-icon class="ps-record-card__close" size="18" @click="isOpen = false">mdi-close</v-icon>
        </div>
        <div class="ps-record-card__body">
          <v-data-table :headers="recordHeaders" :items="records"  item-value="devId"
            class="gm-table ps-record-table" hide-default-footer :items-per-page="pageSize"
            :no-data-text="$str.getKey('参数设置/暂无数据')">
            <!--  eslint-disable-next-line vue/valid-v-slot -->
            <template #item.send="{ item }">
              <div class="ps-record-content">
                <div class="ps-record-content__time">
                  {{ $str.getKey('参数设置/发送时间') }}: {{ item.createTime }}
                </div>
                <div v-if="item.commandContent.eblock_setting">
                  <span class="ps-record-content__k">{{ $str.getKey('参数设置/协议帧') }}: </span>
                  <span class="ps-record-content__v">{{ item.commandContent.eblock_setting }}</span>
                </div>
                <div v-if="item.commandContent.demand_max">
                  <span class="ps-record-content__k">{{ $str.getKey('参数设置/最大需量') }}: </span>
                  <span class="ps-record-content__v">{{ item.commandContent.demand_max }}</span>
                </div>
                <div v-if="item.commandContent.demand_min">
                  <span class="ps-record-content__k">{{ $str.getKey('参数设置/最小需量') }}: </span>
                  <span class="ps-record-content__v">{{ item.commandContent.demand_min }}</span>
                </div>
                <div v-if="item.commandContent.transf_cap_ratio">
                  <span class="ps-record-content__k">{{ $str.getKey('参数设置/箱变容量配比') }}: </span>
                  <span class="ps-record-content__v">{{ item.commandContent.transf_cap_ratio }}</span>
                </div>
                <div v-if="item.commandContent.eblock_enable">
                  <span class="ps-record-content__k">{{ $str.getKey('参数设置/eBlock启用') }}: </span>
                  <span class="ps-record-content__v">{{ item.commandContent.eblock_enable }}</span>
                </div>
                <div v-if="item.commandContent.eblock_disable">
                  <span class="ps-record-content__k">{{ $str.getKey('参数设置/eBlock停用') }}: </span>
                  <span class="ps-record-content__v">{{ item.commandContent.eblock_disable }}</span>
                </div>
                <div v-if="item.commandContent.reactive_mode">
                  <span class="ps-record-content__k">{{ $str.getKey('参数设置/无功工作模式') }}: </span>
                  <span class="ps-record-content__v">{{ reactiveModeDesc(item.commandContent.reactive_mode) }}</span>
                </div>
              </div>
            </template>

            <!--  eslint-disable-next-line vue/valid-v-slot -->
            <template #item.receive="{ item }">
              <template v-if="item.receiveCommandRecord.length > 0">
                <div class="ps-record-content" v-for="(rec, ri) in item.receiveCommandRecord" :key="ri">
                  <div class="ps-record-content__time">
                    {{ $str.getKey('参数设置/接收时间') }}: {{ rec.createTime }}
                  </div>
                  <div v-if="rec.commandContent.demand_max">
                    <span class="ps-record-content__k">{{ $str.getKey('参数设置/最大需量') }}: </span>
                    <span class="ps-record-content__v">{{ rec.commandContent.demand_max }}</span>
                  </div>
                  <div v-if="rec.commandContent.demand_min">
                    <span class="ps-record-content__k">{{ $str.getKey('参数设置/最小需量') }}: </span>
                    <span class="ps-record-content__v">{{ rec.commandContent.demand_min }}</span>
                  </div>
                  <div v-if="rec.commandContent.transf_cap_ratio">
                    <span class="ps-record-content__k">{{ $str.getKey('参数设置/箱变容量配比') }}: </span>
                    <span class="ps-record-content__v">{{ rec.commandContent.transf_cap_ratio }}</span>
                  </div>
                  <div v-if="rec.commandContent.eblock_setting">
                    <span class="ps-record-content__k">{{ $str.getKey('参数设置/协议帧') }}: </span>
                    <span class="ps-record-content__v">{{ rec.commandContent.eblock_setting }}</span>
                  </div>
                  <div v-if="rec.commandContent.eblock_disable_list">
                    <span class="ps-record-content__k">{{ $str.getKey('参数设置/eBlock停用') }}: </span>
                    <span class="ps-record-content__v">{{ rec.commandContent.eblock_disable_list }}</span>
                  </div>
                  <div v-if="rec.commandContent.reactive_mode">
                    <span class="ps-record-content__k">{{ $str.getKey('参数设置/无功工作模式') }}: </span>
                    <span class="ps-record-content__v">{{ reactiveModeDesc(rec.commandContent.reactive_mode) }}</span>
                  </div>
                </div>
              </template>
              <div v-else>--</div>
            </template>

            <!--  eslint-disable-next-line vue/valid-v-slot -->
            <template #item.createBy="{ item }">
              {{ item.createBy || '--' }}
            </template>
          </v-data-table>

          <TablePagination v-model="pageNum" :total="total" :page-size="pageSize" :disabled="loading"
            @change="updateRecords" />
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { onUnmounted, ref, computed, reactive, watch, watchEffect } from 'vue'
import { eui } from 'ems-ui'
import { getElinkStruct } from '@/api/device/elink'
import { getTempatureOption, getTempatureRecord, commandSetting } from '@/api/command'
import EBlockStatus from './eblock-status.vue'
import TablePagination from '@/components/TablePagination/index.vue'
import { ws_req } from '@/ws/wss-request'
import { useRoute } from 'vue-router'

const $str = window.$str
const route = useRoute()
const stationId = computed(() => route.query.stationId)
const eLinks = ref([])
const eLink = ref('')

const isEdit = ref(false)

// 无功工作模式选项
const reactiveModeOptions = computed(() => [
  { value: 1, label: $str.getKey('参数设置/不出无功') },
  { value: 2, label: $str.getKey('参数设置/优先出无功') },
  { value: 3, label: $str.getKey('参数设置/优先出有功') },
])

// 命令记录表头
const recordHeaders = computed(() => [
  { title: 'UUID', key: 'devId', sortable: false, width: '160px' },
  { title: $str.getKey('参数设置/发送内容'), key: 'send', sortable: false },
  { title: $str.getKey('参数设置/接收内容'), key: 'receive', sortable: false },
  { title: $str.getKey('参数设置/操作者'), key: 'createBy', sortable: false, width: '100px' },
])

const temperatureOption = ref([
  { name: '空调制冷开启温度', key: 'start_temp_aircond_cooling', value: '' },
  { name: '空调制冷关闭温度', key: 'stop_temp_aircond_cooling', value: '' },
  { name: '空调制热开启温度', key: 'start_temp_aircond_heating', value: '' },
  { name: '空调制热关闭温度', key: 'stop_temp_aircond_heating', value: '' },
  { name: '水冷制冷开启温度', key: 'temp_water_cooling', value: '' },
  { name: '水冷制冷关闭温度', key: 'temp_water_heating', value: '' },
])


function getDataObj(list, uuid) {
  let data = list.find(item => item.uuid === uuid)
  if (data) {
    let res = {}
    temperatureOption.value.forEach(t => {
      if (t.key in data) {
        res[t.key] = data[t.key]
      }
    })
    return res
  }
}

function watchStation() {
  watch(() => stationId.value, (id) => {
    if (id != null) {
      getElinkStruct(id).then(res => {
        eLinks.value = res.data
        setectFirstELink(eLinks.value)
        clearTimeout(timer)
        updateData(false)
      })
    }
  }, {
    immediate: true
  })
}

watchStation()


function setectFirstELink(eLinks) {
  if (eLinks.length > 0) {
    eLink.value = eLinks[0].uuid
  }
}


function reactiveModeDesc(mode) {
  const Table = {
    1: $str.getKey('参数设置/不出无功'),
    2: $str.getKey('参数设置/优先出无功'),
    3: $str.getKey('参数设置/优先出有功'),
    4: $str.getKey('参数设置/实时控制'),
  }
  return Table[mode] || ''
}


let timer = null
async function updateData(recur = false) {
  clearTimeout(timer)
  let count = 0

  const update = () => {
    if (count >= 5) {
      clearTimeout(timer)
      return
    }

    count++

    getTempatureOption(eLink.value).then(res => {
      setCurrent(res.data)
      if (recur) {
        timer = setTimeout(() => {
          update()
        }, 500)
      }
    })
  }

  update()
}

function setFormValue(data) {
  if (data != null && typeof data == 'object') {
    formValues.demand_max = data.demand_max
    formValues.demand_min = data.demand_min
    formValues.dev_id = data.dev_id
    formValues.uuid = data.uuid
    formValues.transf_cap_ratio = data.transf_cap_ratio
    formValues.reactive_mode = Number(data.reactive_mode)
  } else {
    resetFormValues()
  }
}

function setCurrent(data) {
  let dt = data.find(item => item.uuid === eLink.value)
  setFormValue(dt)
}


function numFormatter(num) {
  return Number(num).toFixed(2)
}

onUnmounted(() => {
  clearTimeout(timer)
})


const pageNum = ref(1)
const pageSize = ref(10)
const total = ref(10)
const records = ref([])
const loading = ref(false)
const isOpen = ref(false)


function updateRecords() {
  loading.value = true

  getTempatureRecord({
    stationId: stationId.value,
    uuid: eLink.value,
    pageNum: pageNum.value,
    pageSize: pageSize.value,
  }).then(res => {
    parseContent(res.rows)
    records.value = res.rows
    total.value = res.total
  }).finally(() => {
    loading.value = false
  })
}


function parseContent(list) {
  try {
    list.forEach(item => {
      item.commandContent = JSON.parse(item.commandContent)
      item.receiveCommandRecord.forEach(rec => {
        rec.commandContent = JSON.parse(rec.commandContent)
      })
    })
  } catch (error) {
    console.warn(error)
  }
}


function openRecord() {
  isOpen.value = true
  updateRecords()
}

async function onSetting() {
  const params = {
    uuid: formValues.uuid,
    dev_id: formValues.dev_id,
    station_id: stationId.value + '',
    eblock_setting: formValues.eblock_setting
  }

  let reg = /^[a-f0-9\s]+$/i
  let valid = reg.test(formValues.eblock_setting)

  if (!valid) {
    eui.showToast($str.getKey('参数设置/协议帧格式有误'), 'error')
    return
  }

  const ok = await eui.confirm($str.getKey('参数设置/系统提示'), $str.getKey('参数设置/确定下发?'))
  if (!ok) return

  try {
    let r = await commandSetting(params)
    if (r.code == 200) {
      eui.showToast($str.getKey('参数设置/操作成功'), 'success')
    }
  } catch (e) {
    console.warn(e)
  }
}


const visible = reactive({
  transf_cap_ratio: false,
  demand_max: false,
  demand_min: false,
  reactive_mode: false,
})


const formValues = reactive({
  "uuid": "",
  "dev_id": "",
  "transf_cap_ratio": '',
  "demand_max": '',
  "demand_min": '',
  "eblock_setting": "",
  "reactive_mode": 1,
})

const settingsValue = reactive({
  "transf_cap_ratio": 0,
  "demand_max": 0,
  "demand_min": 0,
  "reactive_mode": 1
})


function resetFormValues() {
  const obj = {
    "uuid": "",
    "dev_id": "",
    "transf_cap_ratio": '',
    "demand_max": '',
    "demand_min": '',
    "eblock_setting": "",
    "reactive_mode": 1,
  }
  Object.assign(formValues, obj)
}


let eBlockList = ref([])
let eBlockStatus = ref({})

watchEffect(() => {
  if (eLink.value) {
    let cur = eLinks.value.find(elk => elk.uuid == eLink.value)
    if (cur) {
      eBlockList.value = cur.list
      cur.list.map(it => {
        eBlockStatus.value[it.uuid] = 1
      })
      updateFunc()
    } else {
      eBlockList.value = []
      eBlockStatus.value = {}
    }
  }
})


let eb_timer = null
async function updateFunc() {
  clearTimeout(eb_timer)
  let message = await ws_req.request('ws_eblock', { stationId: stationId.value })

  Object.keys(eBlockStatus.value).forEach(uuid => {
    if (Array.isArray(message)) {
      message.forEach(item => {
        if (Array.isArray(item.eblockList)) {
          item.eblockList.forEach(eb => {
            if (eb.uuid == uuid) {
              eBlockStatus.value[uuid] = eb.eblock_disable
            }
          })
        }
      })
    }
  })

  eb_timer = setTimeout(() => {
    updateFunc()
  }, 1000 * 5)
}
watchEffect(() => {
  updateFunc()
})


onUnmounted(() => {
  clearTimeout(eb_timer)
})


function setValue(key) {
  visible[key] = true
  settingsValue[key] = Number(formValues[key])
}

function setReactiveValue() {
  visible['reactive_mode'] = true
  settingsValue.reactive_mode = formValues.reactive_mode
}


function cancelValue(key) {
  visible[key] = false
}

async function confirmValue(key) {
  let curELink = eLinks.value.find(elk => elk.uuid == eLink.value)
  const params = {
    dev_id: curELink.devId,
    uuid: curELink.uuid,
    station_id: stationId.value + '',

    [key]: settingsValue[key],
  }

  try {
    let r = await commandSetting(params)
    if (r.code == 200) {
      eui.showToast($str.getKey('参数设置/操作成功'), 'success')
      updateData(true)
    }
  } catch (error) {
    console.warn(error)
  } finally {
    visible[key] = false
  }
}


async function enableEBlock(eb, enable) {
  let curELink = eLinks.value.find(elk => elk.uuid == eLink.value)

  let key = enable ? 'eblock_enable' : 'eblock_disable'
  let str = enable ? $str.getKey('参数设置/启用') : $str.getKey('参数设置/停用')

  const params = {
    dev_id: curELink.devId,
    uuid: curELink.uuid,
    [key]: eb.devId,
    station_id: stationId.value + '',
  }

  if (!eb.devId) {
    eui.showToast(`${eb.equipmentName} ${$str.getKey('参数设置/的dev_id不存在')}`, 'error')
    return
  }

  const ok = await eui.confirm($str.getKey('参数设置/系统提示'), $str.getKey('参数设置/确定') + ' ' + str + '?')
  if (!ok) return

  try {
    let r = await commandSetting(params)
    if (r.code == 200) {
      eui.showToast($str.getKey('参数设置/操作成功'), 'success')
      setTimeout(() => {
        updateFunc()
      }, 500)
    }
  } catch (e) {
    console.warn(e)
  }
}
</script>


<style lang='scss' scoped>
.params-settings {
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-family: 'HarmonyOS Sans SC', sans-serif;
}

/* 顶部工具栏 */
.ps-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  border-radius: 20px;
  padding: 16px 24px;

  &__left {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__label {
    font-size: 14px;
    color: #2e2e2e;
    white-space: nowrap;
  }
}

.ps-elink-select {
  width: 240px;

  :deep(.v-field) {
    background: #fff;
    border-radius: 20px;
  }
}

/* 面板卡片 */
.ps-panel {
  background: #fff;
  border-radius: 20px;
  padding: 20px 24px;

  &__title {
    display: flex;
    align-items: center;
    font-size: 16px;
    font-weight: 500;
    color: #2e2e2e;
    margin-bottom: 20px;

    &::before {
      content: '';
      width: 4px;
      height: 16px;
      background: #3a68ff;
      border-radius: 2px;
      margin-right: 10px;
    }
  }
}

.ps-param-body {
  display: flex;
  gap: 32px;
  align-items: stretch;

  @media (max-width: 1200px) {
    flex-direction: column;
    gap: 20px;
  }
}

/* 参数列表 */
.ps-param-list {
  flex: 0 0 300px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (max-width: 1200px) {
    flex: 1;
  }
}

.ps-param-item {
  background: rgba(244, 247, 252, 0.6);
  border-radius: 10px;
  padding: 14px 16px;

  &__label {
    font-size: 13px;
    color: #96a5b8;
    margin-bottom: 8px;
  }

  &__value {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 16px;
    color: #2e2e2e;
    font-weight: 500;

    .ps-unit {
      font-size: 13px;
      font-weight: 400;
      color: #96a5b8;
      font-style: normal;
    }
  }
}

.ps-edit-btn {
  text-transform: none;
  letter-spacing: 0;
  min-width: 0;
  padding: 0 8px;
}

/* 协议帧 */
.ps-frame {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;

  &__label {
    font-size: 13px;
    color: #96a5b8;
    margin-bottom: 8px;
  }

  &__input {
    :deep(textarea) {
      font-family: 'Roboto Mono', monospace;
      font-size: 13px;
      line-height: 1.6;
    }
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 14px;
  }
}

/* eBlock 启停 */
.ps-eblock-list {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.ps-eblock-card {
  flex: 0 0 auto;
  min-width: 240px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  background: rgba(244, 247, 252, 0.6);
  border-radius: 12px;
  padding: 14px 18px;

  &__head {
    display: flex;
    align-items: center;
  }

  &__name {
    font-size: 14px;
    color: #2e2e2e;
    font-weight: 500;
  }

  &__actions {
    display: flex;
    gap: 8px;
  }
}

.ps-eblock-btn {
  text-transform: none;
  letter-spacing: 0;
  border-radius: 8px;
}

/* 空状态 */
.ps-empty {
  min-height: 100px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #96a5b8;
  font-size: 14px;
}

/* 通用按钮 */
.ps-btn-outline {
  text-transform: none;
  letter-spacing: 0;
  border-radius: 20px;
  border: 1px solid rgba(192, 207, 218, 0.5);
  color: #3a68ff;
  height: 40px;
}

.ps-btn-primary {
  text-transform: none;
  letter-spacing: 0;
  border-radius: 20px;
  height: 40px;
  min-width: 100px;
  background: #3a68ff !important;
  color: #fff !important;
}

/* 编辑弹出层 */
.ps-edit-pop {
  padding: 16px;
  width: 240px;
  border-radius: 14px !important;
  border: 1px solid rgba(224, 228, 231, 1);
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.12) !important;
  background: #fff;

  &__title {
    font-size: 14px;
    color: #2e2e2e;
    font-weight: 500;
    margin-bottom: 12px;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 12px;

    :deep(.v-btn) {
      text-transform: none;
      letter-spacing: 0;
    }
  }
}

/* 命令记录弹窗 */
.ps-record-card {
  background: linear-gradient(187deg, #bcc9fa 0%, #fff 20%, #fff 100%);

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 24px 12px;
  }

  &__title {
    font-size: 18px;
    font-weight: 500;
    color: #2e2e2e;
  }

  &__close {
    cursor: pointer;
    color: #96a5b8;

    &:hover {
      color: #2e2e2e;
    }
  }

  &__body {
    padding: 0 24px 20px;
  }
}

.ps-record-table {
  background: transparent;
  max-height: 620px;

  :deep(.v-data-table__td) {
    vertical-align: top;
    padding-top: 12px !important;
    padding-bottom: 12px !important;
  }
}

.ps-record-content {
  font-size: 13px;
  color: #444a6d;
  line-height: 1.8;

  &:not(:last-child) {
    padding-bottom: 8px;
    margin-bottom: 8px;
    border-bottom: 1px dashed rgba(192, 202, 226, 0.4);
  }

  &__time {
    color: #96a5b8;
    margin-bottom: 2px;
  }

  &__k {
    color: #96a5b8;
  }

  &__v {
    color: #3a68ff;
    word-break: break-all;
  }
}
</style>
